import type { LibraryBlock } from "../build";
import {
  BLACK_GLASS,
  CARD_SHADOW,
  ellipse,
  icon,
  motion,
  photo,
  pill,
  rect,
  SHADOW,
  shape,
  text,
  WHITE,
} from "./helpers";

// ---------------------------------------------------------------------------
// Zitate & Sprechblasen: speech and thought bubbles, quotes, chat, interviews
// ---------------------------------------------------------------------------

export const QUOTES: LibraryBlock[] = [
  {
    id: "zitat-sprechblase",
    title: "Sprechblase",
    category: "zitat",
    tags: ["Comic", "Blase", "sagen", "Reaktion"],
    width: 600,
    height: 420,
    anchor: "center",
    duration: 3500,
    nodes: [
      ellipse({
        x: 0,
        y: 0,
        w: 600,
        h: 340,
        fill: "background",
        shadow: CARD_SHADOW,
        name: "Blase",
        ...motion("pop"),
      }),
      // Above the bubble, its base inside it: no shadow falls onto the tip.
      shape("triangle", {
        x: 90,
        y: 280,
        w: 110,
        h: 130,
        fill: "background",
        rotation: 160,
        name: "Spitze",
        ...motion("pop"),
      }),
      text({
        text: "Echt jetzt?!",
        style: "heading-1",
        size: 80,
        color: "text",
        x: 70,
        y: 0,
        w: 460,
        h: 340,
        align: "center",
        ...motion("pop", { delay: 150 }),
      }),
    ],
  },
  {
    id: "zitat-gedanke",
    title: "Gedankenblase",
    category: "zitat",
    tags: ["denken", "Gedanke", "Comic", "Idee", "überlegen"],
    width: 640,
    height: 470,
    anchor: "center",
    duration: 4000,
    nodes: [
      ellipse({ x: 20, y: 420, w: 44, h: 44, fill: "background", name: "Punkt", ...motion("pop") }),
      ellipse({
        x: 64,
        y: 336,
        w: 74,
        h: 74,
        fill: "background",
        name: "Punkt",
        ...motion("pop", { delay: 150 }),
      }),
      ellipse({
        x: 100,
        y: 0,
        w: 540,
        h: 330,
        fill: "background",
        shadow: CARD_SHADOW,
        name: "Blase",
        ...motion("pop", { delay: 300 }),
      }),
      text({
        text: "Hmm … was koche ich heute?",
        style: "heading-2",
        size: 50,
        color: "text",
        x: 170,
        y: 0,
        w: 400,
        h: 330,
        align: "center",
        ...motion("fade", { delay: 450 }),
      }),
    ],
  },
  {
    id: "zitat-interview",
    title: "Interview-Frage",
    category: "zitat",
    tags: ["Frage", "Interview", "Podcast", "Q&A", "Gespräch"],
    width: 960,
    height: 300,
    anchor: "top",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 40,
        w: 960,
        h: 260,
        fill: BLACK_GLASS,
        radius: "lg",
        name: "Fläche",
        ...motion("slide-down"),
      }),
      ...pill(40, 0, 220, 80, "FRAGE", "accent", 30, motion("pop", { delay: 150 })),
      text({
        text: "„Was hat dich am meisten überrascht?“",
        style: "heading-2",
        size: 54,
        color: WHITE,
        x: 50,
        y: 120,
        w: 860,
        ...motion("slide-up", { delay: 300 }),
      }),
    ],
  },
  {
    id: "zitat-kundenstimme",
    title: "Kundenstimme",
    category: "zitat",
    tags: ["Bewertung", "Testimonial", "Rezension", "Sterne", "Feedback", "Kunde"],
    width: 920,
    height: 510,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 920,
        h: 510,
        fill: "background/95",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Karte",
        ...motion("slide-up"),
      }),
      ...[0, 1, 2, 3, 4].map((index) =>
        icon({
          icon: "star",
          x: 50 + index * 70,
          y: 50,
          w: 60,
          h: 60,
          color: "accent",
          name: `Stern ${index + 1}`,
          ...motion("pop", { delay: 200 + index * 120 }),
        }),
      ),
      text({
        text: "„Super Beratung, schnelle Lieferung – gerne wieder!“",
        style: "quote",
        size: 46,
        color: "text",
        x: 50,
        y: 140,
        w: 820,
        ...motion("fade", { delay: 700, duration: 700 }),
      }),
      photo({
        x: 50,
        y: 370,
        w: 100,
        h: 100,
        ellipse: true,
        name: "Porträt",
        ...motion("zoom", { delay: 1000 }),
      }),
      text({
        text: "Jana K.",
        style: "heading-2",
        size: 40,
        color: "text",
        x: 175,
        y: 372,
        w: 600,
        ...motion("slide-left", { delay: 1100 }),
      }),
      text({
        text: "Kundin seit 2021",
        style: "caption",
        size: 28,
        color: "text-muted",
        x: 175,
        y: 426,
        w: 600,
        ...motion("slide-left", { delay: 1200 }),
      }),
    ],
  },
  {
    id: "zitat-gross",
    title: "Großes Zitat",
    category: "zitat",
    tags: ["Zitat", "Spruch", "Motivation", "Weisheit"],
    width: 940,
    height: 480,
    anchor: "center",
    duration: 5500,
    nodes: [
      icon({
        icon: "quotes",
        x: 0,
        y: 0,
        w: 140,
        h: 140,
        color: "accent",
        shadow: SHADOW,
        name: "Anführungszeichen",
        ...motion("drop", { duration: 700 }),
      }),
      text({
        text: "Mach es einfach. Perfekt wird es später.",
        style: "quote",
        size: 72,
        color: WHITE,
        shadow: SHADOW,
        x: 0,
        y: 160,
        w: 940,
        ...motion("fade", { delay: 250, duration: 700 }),
      }),
      text({
        text: "– Maxi Mustermann",
        style: "subline",
        size: 36,
        color: "accent",
        shadow: SHADOW,
        x: 0,
        y: 420,
        w: 940,
        ...motion("slide-up", { delay: 650 }),
      }),
    ],
  },
  {
    id: "zitat-chat",
    title: "Chat-Nachrichten",
    category: "zitat",
    tags: ["Chat", "Nachricht", "WhatsApp", "Messenger", "Dialog", "SMS"],
    width: 900,
    height: 450,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 620,
        h: 130,
        fill: "surface",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Nachricht 1",
        ...motion("pop"),
      }),
      text({
        text: "Hast du heute Abend Zeit?",
        style: "body",
        size: 40,
        color: "text",
        x: 36,
        y: 0,
        w: 548,
        h: 130,
        ...motion("pop"),
      }),
      rect({
        x: 280,
        y: 160,
        w: 620,
        h: 130,
        fill: "primary",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Nachricht 2",
        ...motion("pop", { delay: 1000 }),
      }),
      text({
        text: "Klar! Um 19 Uhr beim Italiener?",
        style: "body",
        size: 40,
        color: "on:primary",
        x: 316,
        y: 160,
        w: 548,
        h: 130,
        ...motion("pop", { delay: 1000 }),
      }),
      rect({
        x: 0,
        y: 320,
        w: 440,
        h: 130,
        fill: "surface",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Nachricht 3",
        ...motion("pop", { delay: 2000 }),
      }),
      text({
        text: "Perfekt, bis dann!",
        style: "body",
        size: 40,
        color: "text",
        x: 36,
        y: 320,
        w: 368,
        h: 130,
        ...motion("pop", { delay: 2000 }),
      }),
    ],
  },
];
